import { Box, Typography, Paper, TextField, Grid, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ThemedPagination from './ThemedPagination';
import { useEffect, useMemo, useState } from 'react';
import { getAllOrdini, getKitchenMarches } from '../APIs/OrdersAPI';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { SetupWSEventListener } from '../utils/setupWSEventListener';
import { getTenantTranslation } from '../utils/getTenantTranslation';

//Icons
import SearchIcon from '@mui/icons-material/Search';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useSnackbar } from '../contexts/SnackBarContext';
import {
  getFixedPriceMenuCourses,
  getOrderMenuSearchText,
  getTastingMenuCourses,
  isTastingMenuPackage,
} from '../utils/orderMenuDetails';

function getVariantIds(orderProduct) {
  if (Array.isArray(orderProduct?.variantIds)) {
    return orderProduct.variantIds
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);
  }
  if (Array.isArray(orderProduct?.orderProductVariants)) {
    return orderProduct.orderProductVariants
      .map((opv) => Number(opv?.variantId ?? opv?.variant?.id))
      .filter((id) => Number.isInteger(id) && id > 0);
  }
  const single = Number(orderProduct?.variantId ?? orderProduct?.variant?.id);
  return Number.isInteger(single) && single > 0 ? [single] : [];
}

function getVariantNames(orderProduct) {
  if (Array.isArray(orderProduct?.orderProductVariants)) {
    return orderProduct.orderProductVariants
      .map((opv) => opv?.variant?.name)
      .filter(Boolean);
  }

  if (Array.isArray(orderProduct?.variants)) {
    return orderProduct.variants
      .map((v) => v?.name)
      .filter(Boolean);
  }

  const productVariants = Array.isArray(orderProduct?.product?.variants) ? orderProduct.product.variants : [];
  return getVariantIds(orderProduct)
    .map((id) => productVariants.find((v) => Number(v?.id) === Number(id))?.name)
    .filter(Boolean);
}

function getOrderProductLabel(orderProduct) {
  const baseName = orderProduct?.product?.name || "";
  const variantNames = getVariantNames(orderProduct);
  if (!variantNames.length) return baseName;
  return `${baseName} + ${variantNames.join(" + ")}`;
}

function formatQuantityValue(quantity) {
  const qty = Number(quantity || 0);
  if (!Number.isFinite(qty)) return quantity || "";
  return Number.isInteger(qty)
    ? String(qty)
    : qty.toLocaleString('it-IT', { maximumFractionDigits: 3 });
}

function getProductWeightUnit(product) {
  if (!product?.pricePerWeight) return null;
  return product?.typeOfWeight === 'HECTOGRAM' ? 'hg' : 'kg';
}

function formatOrderProductQuantity(orderProduct) {
  const quantity = formatQuantityValue(orderProduct?.quantity);
  const unit = getProductWeightUnit(orderProduct?.product);
  return unit ? `${quantity} ${unit}` : `${quantity}x`;
}

const orderBelongsToTable = (order, tableId) => {
  if (!tableId) return true;
  if (Number(order?.tableId ?? order?.table?.id) === Number(tableId)) return true;
  return (order?.orderTables || []).some((link) => (
    Number(link?.tableId ?? link?.table?.id) === Number(tableId)
  ));
};

const OrdiniTempoReale = ({ selectedTableId = null }) => {
  const tenantType = localStorage.getItem('tenantType');
  const token = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');
  const theme = useTheme();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();

  const [currentPage, setCurrentPage] = useState(1);
  const [ordiniFiltrati, setOrdiniFiltrati] = useState([])
  const [searchOrdini, setSearchOrdini] = useState("")
  const [marchedKeys, setMarchedKeys] = useState(new Set());

  const ordiniPerPagina = 3
  // const limiteTempo = 2
  const ordiniSearch = useMemo(() => {
    let cerca = searchOrdini.toLowerCase()
    return ordiniFiltrati?.filter((ordine) => {
      if (!orderBelongsToTable(ordine, selectedTableId)) return false;
      let ordini_matchati = (ordine.orderProducts || []).filter((o) =>
        getOrderProductLabel(o).toLowerCase().includes(cerca)
      )
      let pacchetti_matchati = (ordine.orderPackages || []).filter((p) =>
        (p?.package?.name || '').toLowerCase().includes(cerca)
      )
      const menuMatch = getOrderMenuSearchText(ordine).includes(cerca);
      let condizione = (
        ordini_matchati.length > 0 ||
        pacchetti_matchati.length > 0 ||
        menuMatch ||
        ((ordine?.table?.name || '').toLowerCase().includes(cerca))
      )
      return condizione;
    })
  }, [ordiniFiltrati, searchOrdini, selectedTableId])

  const indexOfLast = currentPage * ordiniPerPagina;
  const indexOfFirst = indexOfLast - ordiniPerPagina;
  const ordiniVisibili = ordiniSearch.slice(indexOfFirst, indexOfLast);
  const totalPage = ordiniSearch ? Math.ceil(ordiniSearch.length / ordiniPerPagina) : 0;

  const fetchOrdini = useCallback(async () => {
    getAllOrdini(token, tenant, { noCache: true }).then(async (data) => {
      const ordini = data.filter(order => {
        const orderDate = parseDate(order.createdAt) === parseDate(new Date())
        return orderDate
      })
      const openOrders = filtra_ordini(ordini);
      setOrdiniFiltrati(openOrders);
      try {
        const marches = await getKitchenMarches(openOrders.map((order) => order.id), token, tenant);
        setMarchedKeys(new Set((marches || []).map((entry) => `${entry.orderId}:${entry.lineKey}`)));
      } catch {
        setMarchedKeys(new Set());
      }
    })
  }, [token, tenant])

  useEffect(() => {
    fetchOrdini()
    const cleanup = SetupWSEventListener(fetchOrdini); // setup WS listener
    const onOrderChanged = () => {
      // immediate refresh + delayed refresh for eventual consistency
      fetchOrdini();
      setTimeout(() => fetchOrdini(), 400);
    };
    window.addEventListener('comanda-creata', onOrderChanged);

    return () => {
      window.removeEventListener('comanda-creata', onOrderChanged);
      cleanup();
    };
  }, [fetchOrdini]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTableId]);

  function parseDate(dateStr) {
    try {
      if (!dateStr) throw new Error("Data Nulla o Vuota");
      let dateParsed

      if (typeof (dateStr) == "string") {
        dateParsed = dateStr.toString().trim().split("T")[0]
      } else if (typeof (dateStr) == "object") {
        dateParsed = dateStr.toISOString().trim().split("T")[0]
      } else {
        throw new Error("Formato data non valida");

      }
      return dateParsed;

    } catch (error) {
      handleAlert(error.message, "error")
    }

  }

  function attesa(dateStr) {
    let attesa = 0
    let [ordHour, ordMinute, ordSecond] = dateStr.trim().split("T")[1].trim().split(":")
    let nowHour = new Date().getUTCHours()
    let nowMinute = new Date().getUTCMinutes()

    ordHour = parseInt(ordHour)
    ordMinute = parseInt(ordMinute)

    // if (nowMinute < ordMinute) {
    //   nowHour -= 1
    //   nowMinute += 60
    // }
    // attesa = (nowMinute - ordMinute) + ((nowHour - ordHour) * 60)
    attesa = (nowMinute + nowHour * 60) - (ordMinute + ordHour * 60)
    let strAttesa = attesa.toString() + " " + (attesa === 1 ? t("tableMapPage.minute") : t("tableMapPage.minutes"))

    return [strAttesa, attesa]

  }

  function changeTheme(attesa) {

    if (attesa < 20) {
      return {
        backgroundColor: theme.palette.mode === 'light' ? '#D8FFE5' : '#3F4942',
        padding: 2,
        borderLeft: `8px solid ${theme.palette.mode === 'light' ? '#125605' : '#78BD6A'}`,
        boxShadow: 'none',
      }
    } else if (attesa >= 20 & attesa < 40) {

      return {
        backgroundColor: theme.palette.mode === 'light' ? '#FFF0CF' : '#5B5343',
        padding: 2,
        borderLeft: `8px solid ${theme.palette.mode === 'light' ? '#CC4B00' : '#F9934B'}`,
        boxShadow: 'none',
      }
    } else {
      return {
        backgroundColor: theme.palette.mode === 'light' ? '#FFDEDE' : '#584747',
        borderLeft: `8px solid ${theme.palette.mode === 'light' ? '#E00C0C' : '#FF8C8C'}`,
        padding: 2,
        boxShadow: 'none',
      }
    }
  }
  function changeColorText(attesa) {
    if (attesa < 20) {
      return { color: '#78BD6A' }
    }
    else if (attesa >= 20 & attesa < 40) {
      return { color: '#F9934B' }
    }
    else {
      return { color: '#FF8C8C' }
    }

  }

  function filtra_ordini(ordini) {
    return ordini.filter((filt_order) => {
      let filtro = filt_order.status === "OPEN"
      return filtro
    }).sort((a, b) => (a.createdAt < b.createdAt) ? 1 : ((b.createdAt < a.createdAt) ? -1 : 0))
    // .filter((filt_time) => {
    //   const createdAt = new Date(filt_time.createdAt);
    //   const now = new Date();
    //   const diffMinutes = (now - createdAt) / (1000 * 60);
    //   return diffMinutes <= (limiteTempo * 60);
    // })
  }

  function listaOrdiniTR(ordini) {

    const isMarched = (orderId, lineKey) => marchedKeys.has(`${orderId}:${lineKey}`);
    const marchedRowSx = (marched) => marched ? {
      px: 0.75,
      py: 0.35,
      borderRadius: 1,
      backgroundColor: theme.palette.mode === 'light' ? '#D8FFE5' : '#294232',
      borderLeft: '4px solid #2E7D32',
    } : {};
    const marchBadge = (marched) => marched ? (
      <Chip label="IN MARCIA" color="success" size="small" sx={{ ml: 1, height: 20, fontWeight: 800, fontSize: 10 }} />
    ) : null;

    if (ordini.length > 0) {
      return (ordini.map((order) => {
        return (
          <Paper key={order.id} sx={
            changeTheme(attesa(order.createdAt.toString())[1])
          }>

            <Typography fontWeight="bold">
              {t("client_page.order")} N° {order.id} - {getTenantTranslation(t, tenantType, 'booking.table')} {order.table.name}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                {order.orderProducts.map((ordineProdotto) => {
                  const marched = isMarched(order.id, `product:${ordineProdotto.id}`);
                  return (<Typography key={ordineProdotto.id} sx={{ fontSize: 14, mt: 1, ...marchedRowSx(marched) }}>
                    {`${formatOrderProductQuantity(ordineProdotto)} ${getOrderProductLabel(ordineProdotto)}`}
                    {marchBadge(marched)}
                  </Typography>);
                })}
                {(order.orderPackages || []).map((ordinePacchetto) => {
                  const packageMarched = isMarched(order.id, `package:${ordinePacchetto.id}`);
                  return (<Box key={`pkg-${ordinePacchetto.id}`} sx={{ mt: 1, ...marchedRowSx(packageMarched) }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                      {`${ordinePacchetto.quantity}x ${ordinePacchetto.package?.name || t("orderPackages.label")}`}
                      {marchBadge(packageMarched)}
                    </Typography>
                    {isTastingMenuPackage(ordinePacchetto) && getTastingMenuCourses(ordinePacchetto).map((course) => (
                      <Box key={`pkg-${ordinePacchetto.id}-course-${course.courseIndex}`} sx={{ ml: 1.5, mt: 0.5 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                          {`${t("tableMapPage.course")} ${course.courseIndex}${course.courseName ? ` - ${course.courseName}` : ""}`}
                        </Typography>
                        {course.items.map((item, itemIndex) => {
                          const marched = isMarched(order.id, `package-item:${item.id}`);
                          return (<Typography
                            key={`${item.id || item.name}-${itemIndex}`}
                            sx={{ fontSize: 13, ml: 1.5, ...marchedRowSx(marched) }}
                          >
                            {`${formatQuantityValue(item.quantity)}x ${item.name}${item.variantName ? ` + ${item.variantName}` : ""}`}
                            {marchBadge(marched)}
                          </Typography>);
                        })}
                      </Box>
                    ))}
                  </Box>);
                })}
                {(order.orderFixedPriceMenus || []).map((menu) => (
                  <Box key={`fixed-menu-${menu.id}`} sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                      {`${menu.quantity || 1}x ${menu.menuNameSnapshot || t("fixedPriceMenu.menuFallback")}`}
                    </Typography>
                    {getFixedPriceMenuCourses(menu).map((course) => (
                      <Box key={`fixed-menu-${menu.id}-course-${course.courseIndex}`} sx={{ ml: 1.5, mt: 0.5 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                          {`${t("tableMapPage.course")} ${course.courseIndex}${course.courseName ? ` - ${course.courseName}` : ""}`}
                        </Typography>
                        {course.items.map((item, itemIndex) => {
                          const marched = isMarched(order.id, `fixed:${item.id}`);
                          return (<Typography
                            key={`${item.id || item.name}-${itemIndex}`}
                            sx={{ fontSize: 13, ml: 1.5, ...marchedRowSx(marched) }}
                          >
                            {`${formatQuantityValue(item.quantity)}x ${item.name}`}
                            {marchBadge(marched)}
                          </Typography>);
                        })}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 'bold', mt: 1, textAlign: "end" }}>
                  <span>{t("wait")}: </span><br></br><span style={changeColorText(attesa(order.createdAt.toString())[1])}>{attesa(order.createdAt.toString())[0]}</span>
                </Typography>
              </Box>
            </Box>
          </Paper>
        )

      }
      ))
    }
    else {
      return (
        <Box display={"flex"} alignSelf={"center"} height={"100%"}>
          <Typography sx={{ fontSize: 26, mt: 1, alignContent: "center" }}>
            {t("noOrders")}
          </Typography>
        </Box>

      )
    }


  }

  function handleChangePage(value) {
    setCurrentPage(value)
  }

  const handleSearch = (e) => {
    setSearchOrdini(e.target.value);
  }

  function handleAlert(msg, type) {
    showSnackbar(msg, type)
  }

  return (
    <Box
      sx={{
        border: '1px solid #CCC',
        borderRadius: '8px',
        padding: { xs: 1.25, md: '1.719vw' },
        // height: '537px',
        height: { xs: 'auto', md: '633px' },
        minHeight: { xs: 360, md: '633px' },
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box>
        <Grid container spacing={{ xs: 1, sm: 0 }} alignItems="center">
          <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
            <Typography variant="h6" sx={{ my: "6px" }}> {t("realTimeOrders")} </Typography>
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={4} xl={4} sx={{ display: { xs: 'none', sm: 'block' } }}> <Box></Box> </Grid>
          <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
            <TextField fullWidth placeholder={t('search')}
              value={searchOrdini}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (<SearchIcon sx={{ mr: 1 }} />),
                endAdornment: ((searchOrdini < 1) ? null : <HighlightOffIcon onClick={() => { setSearchOrdini("") }} sx={{ mr: 1 }} />)
              }}
              sx={{
                backgroundColor: theme.checkout.product_button.backgroundColor,
                borderRadius: "8px",
                border: "0px",
                mb: 2
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          // border: "1px solid black"
        }}
      >

        {listaOrdiniTR(ordiniVisibili)}


      </Box>
      <ThemedPagination
        count={totalPage}
        page={currentPage}
        onChange={handleChangePage}
        color="primary"
      />
    </Box>
  );
};

export default OrdiniTempoReale;
